
import { ChatCard } from "@/components/provider/messaging/ChatCard"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useChats } from "@/hooks/useChats"
import { Assessment } from "@/types/database/assessment"
import { isValidProfile } from "@/types/database/profile"
import { ChevronLeft, ChevronRight, Search } from "lucide-react"
import { useState } from "react"

interface MessageListProps {
  onSelectAssessment: (assessment: Assessment) => void
  selectedAssessmentId?: string
}

export const MessageList = ({ onSelectAssessment, selectedAssessmentId }: MessageListProps) => {
  const [filter, setFilter] = useState<"open" | "closed" | "all">("all") // Start with "all" to show everything
  const [searchQuery, setSearchQuery] = useState("")
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20

  const { assessments, totalCount, chatStatuses, unreadCounts, handleMarkComplete, isLoading } = useChats({ 
    page: currentPage, 
    pageSize 
  })

  const totalPages = Math.ceil(totalCount / pageSize)

  const filteredAssessments = assessments.filter((assessment) => {
    // Search filter logic
    let matchesSearch = true
    if (searchQuery.trim()) {
      const profile = assessment.profiles
      if (isValidProfile(profile)) {
        const firstName = profile.first_name?.toLowerCase() || ''
        const lastName = profile.last_name?.toLowerCase() || ''
        const email = profile.email?.toLowerCase() || ''
        const fullName = `${firstName} ${lastName}`.trim()
        const searchTerm = searchQuery.toLowerCase().trim()
        
        matchesSearch = firstName.includes(searchTerm) || 
                       lastName.includes(searchTerm) || 
                       fullName.includes(searchTerm) ||
                       email.includes(searchTerm)
      } else {
        matchesSearch = false
      }
    }
    
    // Status filter logic
    const isComplete = chatStatuses[assessment.id] === true
    const hasUnread = (unreadCounts[assessment.id] || 0) > 0
    
    let matchesStatusFilter = true;
    if (filter === "open") {
      // Open conversations: not marked complete OR has unread messages
      matchesStatusFilter = !isComplete || hasUnread;
    } else if (filter === "closed") {
      // Closed conversations: marked complete AND no unread messages
      matchesStatusFilter = isComplete && !hasUnread;
    }
    // "all" shows everything (matchesStatusFilter stays true)
    
    // Unread filter
    const matchesUnreadFilter = showUnreadOnly ? hasUnread : true
    
    return matchesStatusFilter && matchesUnreadFilter && matchesSearch
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <div className="space-y-4 h-[calc(100vh-6rem)]">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold flex items-center">
          Conversations with Unread Messages
          <span className="ml-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
            {totalCount}
          </span>
        </h2>
        
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <Select value={filter} onValueChange={(value: "open" | "closed" | "all") => setFilter(value)}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="w-full bg-white">
              <SelectItem value="all">All Conversations</SelectItem>
              <SelectItem value="open">Open Conversations</SelectItem>
              <SelectItem value="closed">Closed Conversations</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="unread-only" 
            checked={showUnreadOnly}
            onCheckedChange={(checked) => setShowUnreadOnly(checked === true)}
          />
          <label 
            htmlFor="unread-only" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            Show unread only
          </label>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      
      <ScrollArea className="h-[calc(100vh-20rem)]">
        {isLoading ? (
          <div className="space-y-4 pr-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-md" />
            ))}
          </div>
        ) : (
          <div className="space-y-2 pr-4">
            {filteredAssessments.map((assessment) => (
              <ChatCard
                key={assessment.id}
                assessment={assessment}
                unreadCount={unreadCounts[assessment.id] || 0}
                onMarkComplete={handleMarkComplete}
                onClick={() => onSelectAssessment(assessment)}
                isSelected={selectedAssessmentId === assessment.id}
                isCompleted={chatStatuses[assessment.id] === true}
              />
            ))}
            {filteredAssessments.length === 0 && !isLoading && (
              <div className="text-center p-4 text-muted-foreground">
                {assessments.length === 0 ? (
                  <div>
                    <p>No conversations with unread messages</p>
                    <p className="text-xs mt-2">Conversations appear here when patients send messages you haven't read</p>
                  </div>
                ) : searchQuery ? (
                  <p>No conversations match your search for "{searchQuery}"</p>
                ) : filter === "closed" ? (
                  <p>No closed conversations</p>
                ) : (
                  <p>No conversations match the selected filters</p>
                )}
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
